import React, { useState, useRef, useEffect, useCallback } from 'react';
import { gql, useLazyQuery } from '@apollo/client';
import { X, Search } from 'lucide-react';

const SEARCH_INDUSTRY_BENCHMARKS = gql`
  query SearchIndustryBenchmarks($query: String!) {
    search_industry_benchmarks(query: $query)
  }
`;

const EVIDENCE_STYLES = {
  direct_sector: { label: 'Direct', className: 'text-[hsl(var(--chart-2))] border-[hsl(var(--chart-2))]/30' },
  canadian_transaction: { label: 'Canadian', className: 'text-[hsl(var(--chart-2))] border-[hsl(var(--chart-2))]/30' },
  us_transaction: { label: 'US', className: 'text-[hsl(var(--chart-2))] border-[hsl(var(--chart-2))]/30' },
  public_comps_proxy: { label: 'Market Proxy', className: 'text-[hsl(var(--chart-4))] border-[hsl(var(--chart-4))]/30' },
  broad_fallback: { label: 'Broad', className: 'text-muted-foreground border-border' },
};

/**
 * NAICS industry typeahead search.
 * Searches IndustryValuationBenchmark records by industry_name.
 *
 * Props:
 *   onSelect({ naicsCode, sectorCluster, benchmarkFamily, industryName, evidenceClass })
 *   placeholder
 *   initialValue - pre-fill the input
 */
export default function IndustryTypeahead({ onSelect, placeholder, initialValue }) {
  const [query, setQuery] = useState(initialValue || '');
  const [open, setOpen] = useState(false);
  const [results, setResults] = useState([]);
  const wrapperRef = useRef(null);
  const debounceRef = useRef(null);

  const [searchBenchmarks, { loading }] = useLazyQuery(SEARCH_INDUSTRY_BENCHMARKS, {
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      setResults(data?.search_industry_benchmarks || []);
      setOpen(true);
    },
  });

  const doSearch = useCallback((q) => {
    if (q.length < 2) {
      setResults([]);
      setOpen(false);
      return;
    }
    searchBenchmarks({ variables: { query: q } });
  }, [searchBenchmarks]);

  const handleChange = (e) => {
    const val = e.target.value;
    setQuery(val);

    // Debounce 300ms
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => doSearch(val), 300);
  };

  const handleSelect = (item) => {
    setQuery(item.industry_name);
    setOpen(false);
    if (onSelect) {
      onSelect({
        naicsCode: item.naics_code,
        sectorCluster: item.sector_cluster,
        benchmarkFamily: item.benchmark_family,
        industryName: item.industry_name,
        evidenceClass: item.evidence_class,
      });
    }
  };

  const handleClear = () => {
    setQuery('');
    setResults([]);
    setOpen(false);
  };

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div ref={wrapperRef} className="relative">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <input
          type="text"
          value={query}
          onChange={handleChange}
          onFocus={() => { if (results.length > 0) setOpen(true); }}
          placeholder={placeholder || 'e.g., HVAC services, dental practice...'}
          className="w-full bg-background border border-input rounded-[8px] pl-10 pr-10 py-3 text-foreground placeholder:text-muted-foreground focus:border-[hsl(var(--succeedo-orange))] focus:outline-none"
        />
        {query && (
          <button
            type="button"
            onClick={handleClear}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Dropdown */}
      {open && (
        <div className="absolute top-full mt-1 left-0 right-0 bg-white border border-border rounded-[12px] shadow-xl max-h-[300px] overflow-y-auto z-20">
          {loading && (
            <div className="px-4 py-3 text-[13px] text-muted-foreground">Searching...</div>
          )}
          {!loading && results.length === 0 && query.length >= 2 && (
            <div className="px-4 py-3 text-[13px] text-muted-foreground italic">
              No matching industries found
            </div>
          )}
          {!loading && results.map((item) => {
            const evidence = EVIDENCE_STYLES[item.evidence_class] || EVIDENCE_STYLES.broad_fallback;
            return (
              <button
                key={`${item.naics_code}-${item.benchmark_family}`}
                type="button"
                onClick={() => handleSelect(item)}
                className="w-full text-left px-4 py-3 hover:bg-muted cursor-pointer border-b border-border last:border-0 transition-colors"
              >
                <div className="text-[14px] text-foreground font-medium">
                  {item.industry_name}
                </div>
                <div className="flex items-center gap-3 mt-1">
                  <span className="font-mono text-[11px] text-muted-foreground">
                    NAICS {item.naics_code}
                  </span>
                  <span className={`font-mono text-[10px] uppercase border rounded-full px-2 py-0.5 ${evidence.className}`}>
                    {evidence.label}
                  </span>
                  {item.ebitda_multiple_mid > 0 && (
                    <span className="text-[11px] text-muted-foreground">
                      ~{item.ebitda_multiple_mid.toFixed(1)}x EBITDA
                    </span>
                  )}
                  {/* Only another country's deal data exists for this industry:
                      the badge and multiple are what the valuation will use,
                      a broader sector benchmark or the generic estimate. */}
                  {item.no_local_data && (
                    <span className="text-[11px] text-muted-foreground" data-testid="no-local-data">
                      {item.local_basis === 'generic'
                        ? 'Generic estimate: no deal data for your country yet'
                        : 'Valued on broader sector data'}
                    </span>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
